import { useEffect, useRef, useState } from 'react'
import { Globe, Loader2, X } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useAccountResearch } from '@/hooks/use-api'
import { explainError } from '@/lib/explain-error'
import { CATEGORICAL, GRACKLE_COLORS } from '@/lib/theme'
import type { AccountResearchField, AccountResearchResult } from '@/types'

interface AccountResearchPanelProps {
  /** Applies the fields the user ticked. Called with a partial Account patch. */
  onApply: (values: Record<string, string>) => void
}

/**
 * Confidence styling. Note this maps the opposite way round to
 * DuplicateWarning: there, HIGH confidence means "this is probably a duplicate"
 * and is styled as a warning. Here HIGH means "this value is well supported",
 * so it is styled positively. Both are correct; don't "align" them.
 *
 * Colour never carries the meaning on its own - the label text spells out the
 * confidence level as well.
 */
const CONFIDENCE_STYLE: Record<
  AccountResearchField['confidence'],
  { backgroundColor: string; color: string; borderColor: string }
> = {
  high: {
    backgroundColor: CATEGORICAL.evergreen.tint,
    color: GRACKLE_COLORS.prussianBlue,
    borderColor: CATEGORICAL.evergreen.solid,
  },
  medium: {
    backgroundColor: CATEGORICAL.clay.tint,
    color: GRACKLE_COLORS.prussianBlue,
    borderColor: CATEGORICAL.clay.solid,
  },
  low: {
    backgroundColor: CATEGORICAL.slate.tint,
    color: GRACKLE_COLORS.prussianBlue,
    borderColor: CATEGORICAL.slate.solid,
  },
}

/**
 * Only ever put an https URL in an href. The backend already re-validates every
 * source URL before returning it, so this is defence in depth - but an href is
 * the one place model/API-derived text stops being inert, and a `javascript:`
 * value here would execute. Anything that fails renders as plain text instead.
 */
function isSafeHttpUrl(url: string): boolean {
  try {
    return new URL(url).protocol === 'https:'
  } catch {
    return false
  }
}

const CONFIDENCE_LABEL: Record<AccountResearchField['confidence'], string> = {
  high: 'High confidence',
  medium: 'Medium confidence',
  low: 'Low confidence',
}

/**
 * What to say while the request is in flight.
 *
 * The backend makes ONE synchronous call to Vertex, so no real sub-step events
 * come back — which means these must not claim a step has finished. They
 * describe what the request is doing (true for its whole duration) and escalate
 * as it runs long. The honest liveness signal is the elapsed counter beside
 * them, not a fabricated checklist.
 *
 * `after` is seconds elapsed.
 */
const PROGRESS_PHASES = [
  { after: 0, message: 'Checking the address' },
  { after: 2, message: 'Reading the site' },
  { after: 20, message: 'Still reading — a large site takes longer' },
  {
    after: 45,
    message: 'This is slower than usual. You can cancel and fill the form in yourself',
  },
] as const

function phaseFor(seconds: number): string {
  // Explicitly string: `as const` above narrows the literal type otherwise.
  let current: string = PROGRESS_PHASES[0].message
  for (const phase of PROGRESS_PHASES) {
    if (seconds >= phase.after) current = phase.message
  }
  return current
}

export function AccountResearchPanel({ onApply }: AccountResearchPanelProps) {
  const research = useAccountResearch()
  const abortRef = useRef<AbortController | null>(null)

  const [website, setWebsite] = useState('')
  const [result, setResult] = useState<AccountResearchResult | null>(null)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [status, setStatus] = useState('')
  const [error, setError] = useState('')
  const [cancelled, setCancelled] = useState(false)
  const [elapsed, setElapsed] = useState(0)

  // Real elapsed time, ticking only while a request is open. This is the part
  // that proves the CRM has not hung; the wording beside it is just context.
  useEffect(() => {
    if (!research.isPending) return
    setElapsed(0)
    const started = Date.now()
    const id = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - started) / 1000))
    }, 1000)
    return () => window.clearInterval(id)
  }, [research.isPending])

  const host = (() => {
    const raw = website.trim()
    if (!raw) return ''
    try {
      return new URL(raw.startsWith('http') ? raw : `https://${raw}`).hostname
    } catch {
      return ''
    }
  })()

  // The normalised website is offered as a suggestion too, so the rep doesn't
  // retype into the Website field what they just pasted here. It's derived from
  // their own input, so high confidence is honest - but like everything else it
  // still has to be ticked.
  const fields: AccountResearchField[] = result
    ? [
        {
          field: 'website',
          label: 'Website',
          value: result.website,
          confidence: 'high' as const,
        },
        ...result.fields,
      ]
    : []

  const runResearch = async () => {
    if (!website.trim() || research.isPending) return

    const controller = new AbortController()
    abortRef.current = controller

    setError('')
    setCancelled(false)
    setResult(null)
    // Nothing is ever pre-selected - the rep opts in to each value.
    setSelected(new Set())
    setStatus('')

    try {
      const data = await research.mutateAsync({
        website: website.trim(),
        signal: controller.signal,
      })
      setResult(data)
      // +1 for the normalised website row we add ourselves.
      const shown = data.fields.length + 1
      const attempted = data.pages_attempted?.length ?? 0
      const read = data.sources_read.length
      // Naming the shortfall matters: the usual reason for a thin result is that
      // most pages could not be read, not that the company published nothing.
      const coverage =
        attempted > 0 && read < attempted ? ` Read ${read} of ${attempted} pages.` : ''
      setStatus(
        data.fields.length === 0
          ? `We could only confirm the web address.${coverage} Fill the rest of the form in yourself.`
          : `${shown} suggestions found.${coverage} None are applied until you choose them.`
      )
    } catch (err) {
      if (controller.signal.aborted) {
        setStatus('Research cancelled.')
        setCancelled(true)
        return
      }
      const message = explainError(
        err,
        'Could not read that website. You can still fill the form manually.'
      )
      setError(message)
      setStatus('Research failed.')
    } finally {
      abortRef.current = null
    }
  }

  const cancelResearch = () => {
    abortRef.current?.abort()
  }

  const toggle = (field: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(field)) next.delete(field)
      else next.add(field)
      return next
    })
  }

  const applySelected = () => {
    const values: Record<string, string> = {}
    for (const f of fields) {
      if (selected.has(f.field)) values[f.field] = f.value
    }
    const count = Object.keys(values).length
    if (count === 0) return
    onApply(values)
    setStatus(
      `Applied ${count} field${count === 1 ? '' : 's'} to the form. Review them before saving.`
    )
  }

  const allSelected = fields.length > 0 && selected.size === fields.length

  return (
    <section
      aria-labelledby="account-research-heading"
      className="rounded-md border bg-muted/30 p-4"
    >
      <h3
        id="account-research-heading"
        className="text-sm font-semibold flex items-center gap-2 mb-1"
      >
        <Globe className="h-4 w-4" aria-hidden="true" />
        Research from website
      </h3>
      <p id="account-research-help" className="text-sm text-muted-foreground mb-3">
        Paste the company&apos;s website and we&apos;ll read it for details. Nothing is filled in
        until you choose it.
      </p>

      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[16rem]">
          <Label htmlFor="research-website">Company website</Label>
          <Input
            id="research-website"
            type="url"
            inputMode="url"
            value={website}
            placeholder="https://example.com"
            aria-describedby="account-research-help"
            disabled={research.isPending}
            onChange={(e) => setWebsite(e.target.value)}
            onKeyDown={(e) => {
              // This panel sits inside the account <form>. Without this, Enter
              // would submit the whole form instead of starting the research.
              if (e.key === 'Enter') {
                e.preventDefault()
                void runResearch()
              }
            }}
          />
        </div>
        {/* type="button" on every control here, for the same reason. */}
        <Button
          type="button"
          onClick={() => void runResearch()}
          disabled={research.isPending || !website.trim()}
          aria-label="Research this website and suggest account details"
        >
          {research.isPending && (
            <Loader2
              className="mr-2 h-4 w-4 animate-spin motion-reduce:animate-none"
              aria-hidden="true"
            />
          )}
          {research.isPending ? 'Researching' : 'Research'}
        </Button>
        {research.isPending && (
          <Button
            type="button"
            variant="outline"
            onClick={cancelResearch}
            aria-label="Cancel the website research"
          >
            <X className="mr-2 h-4 w-4" aria-hidden="true" />
            Cancel
          </Button>
        )}
      </div>

      {research.isPending && (
        <>
          <div className="mt-3 flex items-baseline gap-2 text-sm text-muted-foreground">
            <span>
              {phaseFor(elapsed)}
              {host && elapsed >= 2 && elapsed < 20 ? ` at ${host}` : ''}
              <span aria-hidden="true">…</span>
            </span>
            {/* Ticks every second, so it is kept OUT of the live region below and
                hidden from assistive tech: announcing a new number each second
                would make this panel unusable with a screen reader. The phase
                text is announced instead, and only when it changes. */}
            <span
              aria-hidden="true"
              className="font-mono text-xs tabular-nums text-muted-foreground/70"
            >
              {elapsed}s
            </span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            Looking at the home page, and for an about, contact or accessibility page. Nothing is
            saved.
          </p>
        </>
      )}

      {/* Announcement-only: the phase while running, the outcome afterwards.
          Never the per-second counter. */}
      <p role="status" aria-live="polite" className="sr-only">
        {research.isPending ? phaseFor(elapsed) : status}
      </p>

      {!research.isPending && status && (
        <p className="text-sm text-muted-foreground mt-2">{status}</p>
      )}

      {error && (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription role="alert">{error}</AlertDescription>
        </Alert>
      )}

      {!error && !cancelled && fields.length > 0 && (
        <fieldset className="mt-4 border-t pt-3">
          <legend className="text-sm font-medium px-1">
            Suggestions &mdash; tick the ones to apply
          </legend>

          <div className="flex justify-end mb-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() =>
                setSelected(allSelected ? new Set() : new Set(fields.map((f) => f.field)))
              }
              aria-label={allSelected ? 'Clear all suggestions' : 'Select all suggestions'}
            >
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
          </div>

          <ul className="space-y-2 list-none p-0 m-0">
            {fields.map((f) => {
              const id = `research-field-${f.field}`
              return (
                <li key={f.field} className="flex items-start gap-3">
                  <Checkbox
                    id={id}
                    checked={selected.has(f.field)}
                    onCheckedChange={() => toggle(f.field)}
                    className="mt-1 shrink-0"
                  />
                  <Label htmlFor={id} className="font-normal cursor-pointer leading-snug">
                    {/* The accessible name spells out field, value and
                        confidence, so the checkbox is meaningful on its own. */}
                    <span className="font-medium">{f.label}:</span>{' '}
                    <span className="break-words">{f.value}</span>{' '}
                    <span
                      className="ml-1 inline-block rounded-sm border px-1.5 py-0.5 text-xs align-middle"
                      style={CONFIDENCE_STYLE[f.confidence]}
                    >
                      {CONFIDENCE_LABEL[f.confidence]}
                    </span>
                  </Label>
                </li>
              )
            })}
          </ul>

          <div className="mt-3 flex items-center gap-3">
            <Button
              type="button"
              onClick={applySelected}
              disabled={selected.size === 0}
              aria-label={`Apply ${selected.size} selected suggestions to the form`}
            >
              Apply selected{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
            <span className="text-xs text-muted-foreground">
              Applying only fills the form. Nothing is saved until you create the account.
            </span>
          </div>
        </fieldset>
      )}

      {result?.notes && <p className="text-sm text-muted-foreground mt-3">{result.notes}</p>}

      {result && (result.pages_attempted?.length ?? 0) > 0 && (
        <details className="mt-3">
          <summary className="text-sm cursor-pointer">
            Pages read ({result.sources_read.length} of {result.pages_attempted.length})
          </summary>
          <ul className="mt-2 space-y-1 text-sm">
            {result.pages_attempted.map((url) => {
              const wasRead = result.sources_read.includes(url)
              return (
                <li key={url}>
                  {/* Stated in words, not by colour alone. A page shown as not
                      read is the usual explanation for a missing field. */}
                  <span className="mr-1.5 text-xs text-muted-foreground">
                    {wasRead ? 'Read' : 'Not read'}
                  </span>
                  {isSafeHttpUrl(url) ? (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline break-all"
                      aria-label={`Open ${url} in a new tab`}
                    >
                      {url}
                    </a>
                  ) : (
                    // Shown, not linked, so nothing is hidden from the user.
                    <span className="break-all text-muted-foreground">{url}</span>
                  )}
                </li>
              )
            })}
          </ul>
        </details>
      )}
    </section>
  )
}
